import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';

const STORAGE_KEY = 'myleap-ui.sidebarCollapsed';
const SIDER_SELECTOR =
  '.mini-sidebar-toggle.ant-layout-sider, .mini-sidebar.ant-layout-sider';
const TRIGGER_SELECTOR =
  '.app-header-mini-sidebar .trigger2, .app-header-mini .trigger2';

const SidebarCollapseContext = createContext(null);

function readDomCollapsed() {
  const sider = document.querySelector(SIDER_SELECTOR);
  if (!sider) return null;
  return sider.classList.contains('ant-layout-sider-collapsed');
}

function readStoredCollapsed() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === '1') return true;
    if (v === '0') return false;
  } catch {
    /* ignore */
  }
  return true;
}

function clickCremaTrigger() {
  const trigger = document.querySelector(TRIGGER_SELECTOR);
  if (!trigger) return false;

  // Crema uses a react-icons SVG as .trigger2 — SVGElement.click is not
  // always available, so prefer a bubbling MouseEvent for React onClick.
  try {
    if (typeof trigger.click === 'function') {
      trigger.click();
    } else {
      trigger.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
        })
      );
    }
    return true;
  } catch {
    return false;
  }
}

export function SidebarCollapseProvider({ children }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readStoredCollapsed);

  useEffect(() => {
    const sync = () => {
      const fromDom = readDomCollapsed();
      if (fromDom === null) return;
      setSidebarCollapsed((prev) => (prev === fromDom ? prev : fromDom));
    };

    sync();
    const sider = document.querySelector(SIDER_SELECTOR);
    if (!sider) {
      const boot = window.setInterval(() => {
        if (document.querySelector(SIDER_SELECTOR)) {
          window.clearInterval(boot);
          sync();
        }
      }, 100);
      return () => window.clearInterval(boot);
    }

    const observer = new MutationObserver(sync);
    observer.observe(sider, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, sidebarCollapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
    document.documentElement.dataset.leapSidebar =
      sidebarCollapsed ? 'collapsed' : 'expanded';
  }, [sidebarCollapsed]);

  const toggleSidebarCollapsed = useCallback(() => {
    if (!clickCremaTrigger()) {
      setSidebarCollapsed((v) => !v);
    }
  }, []);

  const value = useMemo(
    () => ({
      sidebarCollapsed,
      toggleSidebarCollapsed,
    }),
    [sidebarCollapsed, toggleSidebarCollapsed]
  );

  return (
    <SidebarCollapseContext.Provider value={value}>
      {children}
    </SidebarCollapseContext.Provider>
  );
}

SidebarCollapseProvider.propTypes = {
  children: PropTypes.node,
};

export function useSidebarCollapse() {
  const ctx = useContext(SidebarCollapseContext);
  if (!ctx) {
    throw new Error('useSidebarCollapse must be used within SidebarCollapseProvider');
  }
  return ctx;
}

export function useSidebarCollapseOptional() {
  return useContext(SidebarCollapseContext);
}
